import { DetailForm } from '@/@diffrag/form/DetailForm'
import { FormFieldType } from '@/@diffrag/form/fields/type'
import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentDetailView } from '@/@diffrag/layout/ContentDetailView'
import { api } from '@/api'
import type { IAdminReferralPartnerSave, IReferralPartnerRO } from 'api'
import { useNavigate, Navigate } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { useUser } from '@/@diffrag/providers/user.provider'
import { AdminRole, type AdminJwtUser } from '@/constants/adminRole'
import { toast } from '@/lib/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useFormContext } from 'react-hook-form'
import type { FieldValues, UseFormReturn } from 'react-hook-form'
import { PartnerAdminCreateDialog } from './PartnerAdminCreateDialog'
import { SERVICE_DOMAIN } from '@/constants/serviceDomain'
import {
  DEFAULT_SITE_LOCALE,
  SITE_LOCALE_OPTIONS,
} from '@/constants/siteLocales'
import { IAdminReferralPartnerSaveDefaultLocaleEnum } from 'api'

const OWNER_ADMIN_NONE = '__none__'

function buildReferralUrls(code: string, locale: string) {
  const trimmedCode = code.trim()
  const loc = locale || DEFAULT_SITE_LOCALE
  return {
    home: `${SERVICE_DOMAIN}/${loc}?ref=${encodeURIComponent(trimmedCode)}`,
    short: `${SERVICE_DOMAIN}/r/${encodeURIComponent(trimmedCode)}`,
    consultation: `${SERVICE_DOMAIN}/${loc}/consultation?ref=${encodeURIComponent(trimmedCode)}`,
  }
}

type CodeCheckStatus = 'idle' | 'available' | 'duplicate'

function ReferralPartnerSaveGate({
  codeCheckStatus,
  lastCheckedCode,
  onCanSaveChange,
}: {
  codeCheckStatus: CodeCheckStatus
  lastCheckedCode: string | null
  onCanSaveChange: (canSave: boolean) => void
}) {
  const form = useFormContext()
  const code = form.watch('code')
  const status = form.watch('status')

  useEffect(() => {
    const trimmedCode = String(code ?? '').trim()
    const hasRequired = trimmedCode.length > 0 && !!status
    const hasValidCheck =
      codeCheckStatus === 'available' && lastCheckedCode === trimmedCode
    onCanSaveChange(hasRequired && hasValidCheck)
  }, [code, status, codeCheckStatus, lastCheckedCode, onCanSaveChange])

  return null
}

function ReferralUrlPreview({ fallbackCode }: { fallbackCode: string }) {
  const form = useFormContext()
  const code = String(form.watch('code') ?? fallbackCode ?? '').trim()
  const locale =
    String(form.watch('defaultLocale') ?? DEFAULT_SITE_LOCALE) ||
    DEFAULT_SITE_LOCALE
  const previewUrls = code ? buildReferralUrls(code, locale) : null

  return (
    <div className="mb-16 rounded-8 border border-gray-200 bg-gray-50 p-16 text-14">
      <p className="font-bold">URL 안내</p>
      {previewUrls ? (
        <>
          <p className="mt-12 font-medium">레퍼럴 URL</p>
          <p className="mt-4 break-all">{previewUrls.home}</p>
          <p className="mt-4 break-all">{previewUrls.short}</p>
          <p className="mt-12 font-medium">상담 신청 폼</p>
          <p className="mt-4 break-all">{previewUrls.consultation}</p>
        </>
      ) : (
        <p className="mt-8 text-gray-600">
          코드 입력 후 중복 검사·저장하면 레퍼럴 URL이 발급됩니다.
        </p>
      )}
    </div>
  )
}

export default function ReferralPartnerEdit() {
  const navigate = useNavigate()
  const id = useQueryValue('id', '')
  const user = useUser() as AdminJwtUser | undefined
  const isSuperAdmin = user?.role !== AdminRole.PARTNER_ADMIN
  const isReadOnly = !isSuperAdmin

  if (isReadOnly && !id) {
    return <Navigate to="/referral-partners" replace />
  }
  const [codeCheckStatus, setCodeCheckStatus] =
    useState<CodeCheckStatus>('idle')
  const [isCheckingCode, setIsCheckingCode] = useState(false)
  const [lastCheckedCode, setLastCheckedCode] = useState<string | null>(null)
  const [canSave, setCanSave] = useState(false)
  const [extraAdminOptions, setExtraAdminOptions] = useState<
    { label: string; value: string }[]
  >([])

  const { data: admins, refetch: refetchAdmins } = useApi({
    id: 'partnerAdminsForReferral',
    fn: api().admin.getAdmins,
    params: {},
    enabled: isSuperAdmin,
  })

  const { data, refetch } = useApi({
    id: 'referralPartnerDetail',
    fn: api().adminReferralPartner.adminReferralPartnerDetail,
    params: Number(id),
    enabled: !!id,
  })

  const partnerAdminOptions =
    admins
      ?.filter((admin) => admin.role === AdminRole.PARTNER_ADMIN)
      .map((admin) => ({
        label: `${admin.name} (${admin.username})`,
        value: String(admin.id),
      })) ?? []

  const mergedPartnerAdminOptions = [
    ...partnerAdminOptions,
    ...extraAdminOptions.filter(
      (extra) =>
        !partnerAdminOptions.some((item) => item.value === extra.value),
    ),
  ]

  const renderOwnerAdminField = useCallback(
    (_data: unknown, context?: UseFormReturn<FieldValues>) => {
      const value = String(context?.watch('ownerAdminId') ?? OWNER_ADMIN_NONE)

      return (
        <div className="flex max-w-[500px] flex-col gap-8">
          <Select
            value={value}
            onValueChange={(next) => context?.setValue('ownerAdminId', next)}
          >
            <SelectTrigger>
              <SelectValue placeholder="소유 파트너 관리자" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={OWNER_ADMIN_NONE}>(없음)</SelectItem>
              {mergedPartnerAdminOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <PartnerAdminCreateDialog
            onCreated={(admin) => {
              const option = {
                label: `${admin.name} (${admin.username})`,
                value: String(admin.id),
              }
              setExtraAdminOptions((prev) => [...prev, option])
              void refetchAdmins()
              context?.setValue('ownerAdminId', option.value)
            }}
          />
        </div>
      )
    },
    [mergedPartnerAdminOptions, refetchAdmins],
  )

  const resetCodeCheck = useCallback(() => {
    setCodeCheckStatus('idle')
    setLastCheckedCode(null)
  }, [])

  const handleCanSaveChange = useCallback((value: boolean) => {
    setCanSave(value)
  }, [])

  const verifyCodeAvailable = useCallback(
    async (code: string, notify: boolean) => {
      if (!code) {
        resetCodeCheck()
        if (notify) {
          toast.error('코드를 입력해주세요.')
        }
        return false
      }

      setIsCheckingCode(true)
      try {
        const check = await api().adminReferralPartner.checkCodeList({
          code,
          excludeId: id ? Number(id) : undefined,
        })

        if (check.available) {
          setCodeCheckStatus('available')
          setLastCheckedCode(code)
          if (notify) {
            toast.success('사용 가능한 코드입니다.')
          }
          return true
        }

        setCodeCheckStatus('duplicate')
        setLastCheckedCode(null)
        if (notify) {
          toast.error(check.message ?? '이미 사용 중인 코드입니다.')
        }
        return false
      } catch {
        resetCodeCheck()
        if (notify) {
          toast.error('중복 검사에 실패했습니다.')
        }
        return false
      } finally {
        setIsCheckingCode(false)
      }
    },
    [id, resetCodeCheck],
  )

  const onClickCheckDuplicate = (code: string) => {
    resetCodeCheck()
    void verifyCodeAvailable(code, true)
  }

  const renderCodeField = useCallback(
    (_data: unknown, context?: UseFormReturn<FieldValues>) => {
      const registration = context?.register('code', { required: true })

      return (
        <div className="w-full">
          <div className="flex w-fit max-w-full items-center gap-8">
            <Input
              className="w-[280px] max-w-full"
              placeholder="예: PARTNER_A01"
              {...registration}
              onChange={(event) => {
                registration?.onChange(event)
                resetCodeCheck()
              }}
            />
            <Button
              type="button"
              variant="outline"
              className="shrink-0"
              disabled={
                isCheckingCode || !String(context?.watch('code') ?? '').trim()
              }
              onClick={() => {
                onClickCheckDuplicate(
                  String(context?.getValues('code') ?? '').trim(),
                )
              }}
            >
              {isCheckingCode ? '검사 중…' : '중복 검사'}
            </Button>
          </div>
          {codeCheckStatus === 'available' && (
            <p className="mt-6 text-12 text-green-600">
              사용 가능한 코드입니다.
            </p>
          )}
          {codeCheckStatus === 'duplicate' && (
            <p className="mt-6 text-12 text-red-600">
              이미 사용 중인 코드입니다.
            </p>
          )}
        </div>
      )
    },
    [codeCheckStatus, isCheckingCode, resetCodeCheck],
  )

  const onSave = async (formData: IReferralPartnerRO) => {
    const trimmedCode = formData.code.trim()
    if (!trimmedCode) {
      toast.error('코드를 입력해주세요.')
      return
    }

    if (codeCheckStatus !== 'available' || lastCheckedCode !== trimmedCode) {
      toast.error('저장 전에 중복 검사를 진행해주세요.')
      return
    }

    try {
      const saveData: IAdminReferralPartnerSave = {
        code: trimmedCode,
        status: formData.status,
        defaultLocale:
          (formData.defaultLocale as unknown as IAdminReferralPartnerSaveDefaultLocaleEnum) ??
          DEFAULT_SITE_LOCALE,
        memo: formData.memo ?? undefined,
        contactEmail: formData.contactEmail ?? undefined,
        ...(isSuperAdmin
          ? {
              ownerAdminId:
                formData.ownerAdminId &&
                String(formData.ownerAdminId) !== OWNER_ADMIN_NONE
                  ? Number(formData.ownerAdminId)
                  : null,
            }
          : {}),
      }

      if (id) {
        await api().adminReferralPartner.adminReferralPartnerUpdate(
          Number(id),
          saveData,
        )
      } else {
        await api().adminReferralPartner.adminReferralPartnerCreate(saveData)
      }

      toast.success(
        id ? '레퍼럴 URL을 수정했습니다.' : '레퍼럴 URL을 발급했습니다.',
      )
      navigate('/referral-partners', { replace: true })
      refetch()
    } catch (error: unknown) {
      const message =
        (error as { error?: { message?: string } })?.error?.message ??
        '저장에 실패했습니다.'
      toast.error(message)
    }
  }

  const previewCode = data?.code ?? ''

  return (
    <ContentDetailView
      title={
        isReadOnly
          ? '레퍼럴 URL 상세'
          : id
            ? '레퍼럴 URL 수정'
            : '레퍼럴 URL 발급'
      }
      data={data}
      onSave={isReadOnly ? undefined : onSave}
      hideSaveToast={() => true}
      saveDisabled={!canSave}
      cancel
    >
      {!isReadOnly && (
        <ReferralPartnerSaveGate
          codeCheckStatus={codeCheckStatus}
          lastCheckedCode={lastCheckedCode}
          onCanSaveChange={handleCanSaveChange}
        />
      )}
      {isReadOnly ? (
        <div className="mb-16 rounded-8 border border-gray-200 bg-gray-50 p-16 text-14">
          <p className="font-bold">URL 안내</p>
          {previewCode ? (
            <>
              {(() => {
                const previewUrls = buildReferralUrls(
                  previewCode,
                  data?.defaultLocale ?? DEFAULT_SITE_LOCALE,
                )
                return (
                  <>
                    <p className="mt-12 font-medium">레퍼럴 URL</p>
                    <p className="mt-4 break-all">{previewUrls.home}</p>
                    <p className="mt-4 break-all">{previewUrls.short}</p>
                    <p className="mt-12 font-medium">상담 신청 폼</p>
                    <p className="mt-4 break-all">{previewUrls.consultation}</p>
                  </>
                )
              })()}
            </>
          ) : (
            <p className="mt-8 text-gray-600">URL 정보를 불러오는 중입니다.</p>
          )}
        </div>
      ) : (
        <ReferralUrlPreview fallbackCode={previewCode} />
      )}
      <DetailForm
        data={
          data
            ? {
                ...data,
                ownerAdminId: data.ownerAdminId
                  ? String(data.ownerAdminId)
                  : OWNER_ADMIN_NONE,
              }
            : {
                code: '',
                status: 'ACTIVE',
                defaultLocale: DEFAULT_SITE_LOCALE,
                memo: '',
                contactEmail: '',
                ownerAdminId: OWNER_ADMIN_NONE,
              }
        }
        fields={
          isReadOnly
            ? [
                {
                  label: '코드',
                  key: 'code',
                  type: FormFieldType.STRING,
                  readOnly: true,
                },
                {
                  label: '기본 언어',
                  key: 'defaultLocale',
                  type: FormFieldType.SELECT,
                  readOnly: true,
                  options: SITE_LOCALE_OPTIONS,
                },
                {
                  label: '상태',
                  key: 'status',
                  type: FormFieldType.SELECT,
                  readOnly: true,
                  options: [
                    { label: '활성', value: 'ACTIVE' },
                    { label: '비활성', value: 'INACTIVE' },
                  ],
                },
                {
                  label: '연락 이메일',
                  key: 'contactEmail',
                  type: FormFieldType.STRING,
                  readOnly: true,
                },
                {
                  label: '메모',
                  key: 'memo',
                  type: FormFieldType.TEXT,
                  readOnly: true,
                },
              ]
            : [
                {
                  label: '코드',
                  key: 'code',
                  type: FormFieldType.CUSTOM_READ_ONLY,
                  required: true,
                  render: renderCodeField,
                },
                {
                  label: '기본 언어',
                  key: 'defaultLocale',
                  type: FormFieldType.SELECT,
                  required: true,
                  options: SITE_LOCALE_OPTIONS,
                },
                ...(isSuperAdmin
                  ? [
                      {
                        label: '소유 파트너 관리자',
                        key: 'ownerAdminId',
                        type: FormFieldType.CUSTOM_READ_ONLY,
                        render: renderOwnerAdminField,
                      },
                    ]
                  : []),
                {
                  label: '상태',
                  key: 'status',
                  type: FormFieldType.SELECT,
                  required: true,
                  options: [
                    { label: '활성', value: 'ACTIVE' },
                    { label: '비활성', value: 'INACTIVE' },
                  ],
                },
                {
                  label: '연락 이메일',
                  key: 'contactEmail',
                  type: FormFieldType.STRING,
                },
                {
                  label: '메모',
                  key: 'memo',
                  type: FormFieldType.TEXT,
                },
              ]
        }
      />
    </ContentDetailView>
  )
}
